import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiClient } from "./client";
import type { components } from "./schema";
import { useOptimisticListMutation } from "./use-optimistic-mutation";
import { captureWorkspaceListQueryError } from "./workspace-list-sentry";

export interface WorkspaceAuditParams {
  limit?: number;
  offset?: number;
}

export const queryKeys = {
  me: ["me"] as const,
  workspaces: {
    all: ["workspaces"] as const,
    list: () => ["workspaces", "list"] as const,
    detail: (uuid: string) => ["workspaces", "detail", uuid] as const,
    members: (uuid: string) => ["workspaces", "members", uuid] as const,
    invites: (uuid: string) => ["workspaces", "invites", uuid] as const,
    audit: (uuid: string, params?: WorkspaceAuditParams) =>
      ["workspaces", "audit", uuid, params ?? null] as const,
    projects: {
      all: (workspaceUuid: string) =>
        ["workspaces", "projects", workspaceUuid] as const,
      listAll: (workspaceUuid: string) =>
        ["workspaces", "projects", workspaceUuid, "list"] as const,
      list: (workspaceUuid: string, q?: string | null) =>
        ["workspaces", "projects", workspaceUuid, "list", q ?? null] as const,
      detail: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "projects",
          workspaceUuid,
          "detail",
          projectUuid,
        ] as const,
    },
    bosunProjects: {
      all: (workspaceUuid: string) =>
        ["workspaces", "bosun-projects", workspaceUuid] as const,
      list: (workspaceUuid: string) =>
        ["workspaces", "bosun-projects", workspaceUuid, "list"] as const,
      detail: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "detail",
          projectUuid,
        ] as const,
      pmProfile: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "pm-profile",
          projectUuid,
        ] as const,
      goals: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "goals",
          projectUuid,
        ] as const,
      goalsAudit: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "goals-audit",
          projectUuid,
        ] as const,
      releases: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "detail",
          projectUuid,
          "releases",
        ] as const,
      kanbanSummary: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "kanban-summary",
        ] as const,
      kanbanTasks: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "kanban-tasks",
        ] as const,
      intakeRequests: (workspaceUuid: string, projectUuid?: string | null) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          "intake-requests",
          projectUuid ?? null,
        ] as const,
      pmThread: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "pm-thread",
        ] as const,
      pmThreadConversation: (
        workspaceUuid: string,
        projectUuid: string,
        conversationUuid?: string | null,
      ) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "pm-thread",
          conversationUuid ?? "default",
        ] as const,
      pmConversations: (workspaceUuid: string, projectUuid: string) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "pm-conversations",
        ] as const,
      actionItems: (
        workspaceUuid: string,
        projectUuid: string,
        status?: string | null,
      ) =>
        [
          "workspaces",
          "bosun-projects",
          workspaceUuid,
          projectUuid,
          "action-items",
          status ?? null,
        ] as const,
    },
    bosunCredentials: (workspaceUuid: string) =>
      ["workspaces", "bosun-credentials", workspaceUuid] as const,
    bosunTokenInventory: (workspaceUuid: string) =>
      ["workspaces", "bosun-token-inventory", workspaceUuid] as const,
  },
  bosun: {
    cockpit: (includeArchived?: boolean) =>
      ["bosun", "cockpit", includeArchived ?? false] as const,
    actionItems: (status?: string | null, includeDone?: boolean) =>
      ["bosun", "action-items", status ?? null, includeDone ?? false] as const,
  },
} as const;

export type DashboardMe = components["schemas"]["DashboardMe"];
export type DashboardMeUpdate = components["schemas"]["DashboardMeUpdate"];
export type WorkspaceRead = components["schemas"]["WorkspaceRead"];
export type WorkspaceCreate = components["schemas"]["WorkspaceCreate"];
export type WorkspaceUpdate = components["schemas"]["WorkspaceUpdate"];
export type WorkspaceMemberRead = components["schemas"]["WorkspaceMemberRead"];
export type WorkspaceMemberUpdate =
  components["schemas"]["WorkspaceMemberUpdate"];
export type WorkspaceInviteRead = components["schemas"]["WorkspaceInviteRead"];
export type WorkspaceInviteCreate =
  components["schemas"]["WorkspaceInviteCreate"];
export type WorkspaceInviteCreateResponse =
  components["schemas"]["WorkspaceInviteCreateResponse"];
export type WorkspaceInviteAcceptRequest =
  components["schemas"]["WorkspaceInviteAcceptRequest"];
export type WorkspaceRole = components["schemas"]["WorkspaceRole"];
export type ProjectRead = components["schemas"]["ProjectRead"];
export type ProjectCreate = components["schemas"]["ProjectCreate"];
export type ProjectUpdate = components["schemas"]["ProjectUpdate"];
export type AuditLogRead = components["schemas"]["AuditLogRead"];
export type BosunProjectCreate = components["schemas"]["BosunProjectCreate"];
export type BosunOrgCredentials = components["schemas"]["BosunOrgCredentials"];
export type BosunWorkspaceCredentialValidationRead =
  components["schemas"]["BosunWorkspaceCredentialValidationRead"];
export type BosunWorkspaceCredentialStatusRead =
  components["schemas"]["BosunWorkspaceCredentialStatusRead"];
export type BosunTokenInventoryRead =
  components["schemas"]["BosunTokenInventoryRead"];
export type BosunProjectRead = components["schemas"]["BosunProjectRead"];
export type BosunCodexSetupRequest =
  components["schemas"]["BosunCodexSetupRequest"];
export type BosunCodexSetupRead =
  components["schemas"]["BosunCodexSetupRead"];
export type BosunSlackAppTokenSubmitRequest =
  components["schemas"]["BosunSlackAppTokenSubmitRequest"];
export type BosunProjectLifecycleRead =
  components["schemas"]["BosunProjectLifecycleRead"];
export type BosunProjectDeleteRequest =
  components["schemas"]["BosunProjectDeleteRequest"];
export type BosunProjectDeleteRead =
  components["schemas"]["BosunProjectDeleteRead"];
export type BosunProjectManagerProfileRead =
  components["schemas"]["BosunProjectManagerProfileRead"];
export type BosunProjectManagerProfileUpdate =
  components["schemas"]["BosunProjectManagerProfileUpdate"];
export type BosunProjectSettingsUpdate =
  components["schemas"]["BosunProjectSettingsUpdate"];
export type BosunPmChatMessage = components["schemas"]["BosunPmChatMessage"];
export type BosunPmChatRequest = components["schemas"]["BosunPmChatRequest"];
export type BosunPmChatResponse = components["schemas"]["BosunPmChatResponse"];
export type BosunCockpitRead = components["schemas"]["BosunCockpitRead"];
export type BosunCockpitProjectSummaryRead =
  components["schemas"]["BosunCockpitProjectSummaryRead"];
export type BosunIntakeRequestCreate =
  components["schemas"]["BosunIntakeRequestCreate"];
export type BosunIntakeRequestRead =
  components["schemas"]["BosunIntakeRequestRead"];
export type BosunPmThreadRead = components["schemas"]["BosunPmThreadRead"];
export type BosunPmConversationRead =
  components["schemas"]["BosunPmConversationRead"];
export type BosunPmConversationCreate =
  components["schemas"]["BosunPmConversationCreate"];
export type BosunPmMessageCreate =
  components["schemas"]["BosunPmMessageCreate"];
export type BosunPmMessageRead = components["schemas"]["BosunPmMessageRead"];
export type BosunPmMessageResponse =
  components["schemas"]["BosunPmMessageResponse"];
export type BosunUserActionItemCreate =
  components["schemas"]["BosunUserActionItemCreate"];
export type BosunUserActionItemUpdate =
  components["schemas"]["BosunUserActionItemUpdate"];
export type BosunUserActionItemResolve =
  components["schemas"]["BosunUserActionItemResolve"];
export type BosunUserActionItemRead =
  components["schemas"]["BosunUserActionItemRead"];
export type BosunProjectGoalsRead =
  components["schemas"]["BosunProjectGoalsRead"];
export type BosunProjectGoalsUpdate =
  components["schemas"]["BosunProjectGoalsUpdate"];
export type GitHubReleaseRead = components["schemas"]["GitHubReleaseRead"];
export type BosunKanbanSummaryRead =
  components["schemas"]["BosunKanbanSummaryRead"];
export type BosunKanbanTaskRead = components["schemas"]["BosunKanbanTaskRead"];
export type JobRead = components["schemas"]["JobRead"];

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async (): Promise<DashboardMe> => {
      const { data, error } = await apiClient.GET("/dashboard/me");
      if (error) throw new ApiError(error);
      return data;
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DashboardMeUpdate): Promise<DashboardMe> => {
      const { data, error } = await apiClient.PATCH("/dashboard/me", { body });
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (data) => qc.setQueryData(queryKeys.me, data),
  });
}

export function useIsReadOnly(): boolean {
  const { data } = useMe();
  return data?.is_active === false;
}

function responseStatusFromResult(result: unknown): number | undefined {
  if (typeof result !== "object" || result === null) return undefined;
  if (!("response" in result)) return undefined;
  const response = result.response;
  if (response instanceof Response) return response.status;
  if (typeof response !== "object" || response === null) return undefined;
  const status = (response as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

export function useWorkspaces() {
  const query = useQuery({
    queryKey: queryKeys.workspaces.list(),
    queryFn: async (): Promise<WorkspaceRead[]> => {
      const result = await apiClient.GET("/dashboard/workspaces");
      const { data, error } = result;
      if (error) throw new ApiError(error, responseStatusFromResult(result));
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!query.isError) return;
    captureWorkspaceListQueryError(query.error, {
      retryCount: query.failureCount,
    });
  }, [query.error, query.failureCount, query.isError]);

  return query;
}

export function useWorkspace(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.detail(workspaceUuid ?? ""),
    queryFn: async (): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}",
        {
          params: { path: { workspace_uuid: workspaceUuid! } },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid),
  });
}

const OPTIMISTIC_WORKSPACE_UUID_PREFIX = "optimistic-";

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useOptimisticListMutation<
    WorkspaceRead,
    WorkspaceCreate,
    WorkspaceRead
  >({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST("/dashboard/workspaces", {
        body,
      });
      if (error) throw new ApiError(error);
      return data;
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, body) => [
      ...current,
      {
        uuid: `${OPTIMISTIC_WORKSPACE_UUID_PREFIX}${Date.now()}`,
        name: body.name,
        slug: body.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        is_personal: false,
        archive_status: "active",
        archived_at: null,
        slack_default_invite_user_ids: [],
        role: "owner" as WorkspaceRole,
        member_count: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
    onSuccess: (data) => {
      qc.setQueryData<WorkspaceRead[]>(
        queryKeys.workspaces.list(),
        (current) => {
          const withoutPlaceholder = (current ?? []).filter(
            (w) => !w.uuid.startsWith(OPTIMISTIC_WORKSPACE_UUID_PREFIX),
          );
          return withoutPlaceholder.some((w) => w.uuid === data.uuid)
            ? withoutPlaceholder
            : [...withoutPlaceholder, data];
        },
      );
    },
  });
}

export function useUpdateWorkspace(workspaceUuid: string) {
  const qc = useQueryClient();
  return useOptimisticListMutation<
    WorkspaceRead,
    WorkspaceUpdate,
    WorkspaceRead
  >({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}",
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, body) =>
      current.map((w) =>
        w.uuid === workspaceUuid
          ? {
              ...w,
              name: body.name ?? w.name,
              slack_default_invite_user_ids:
                body.slack_default_invite_user_ids ?? w.slack_default_invite_user_ids,
              updated_at: new Date().toISOString(),
            }
          : w,
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.detail(workspaceUuid),
      }),
  });
}

export function useDeleteWorkspace() {
  return useOptimisticListMutation<void, string, WorkspaceRead>({
    mutationFn: async (workspaceUuid) => {
      const { error } = await apiClient.DELETE(
        "/dashboard/workspaces/{workspace_uuid}",
        {
          params: { path: { workspace_uuid: workspaceUuid } },
        },
      );
      if (error) throw new ApiError(error);
    },
    queryKey: queryKeys.workspaces.list(),
    applyOptimistic: (current, uuid) => current.filter((w) => w.uuid !== uuid),
  });
}

function upsertWorkspaceCache(qc: ReturnType<typeof useQueryClient>, workspace: WorkspaceRead) {
  qc.setQueryData<WorkspaceRead[]>(
    queryKeys.workspaces.list(),
    (current) =>
      (current ?? []).map((item) =>
        item.uuid === workspace.uuid ? workspace : item,
      ),
  );
  qc.setQueryData(queryKeys.workspaces.detail(workspace.uuid), workspace);
}

export function useArchiveWorkspace(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/archive",
        { params: { path: { workspace_uuid: workspaceUuid } } },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (workspace) => upsertWorkspaceCache(qc, workspace),
  });
}

export function useUnarchiveWorkspace(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/unarchive",
        { params: { path: { workspace_uuid: workspaceUuid } } },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (workspace) => upsertWorkspaceCache(qc, workspace),
  });
}

export function useWorkspaceMembers(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.members(workspaceUuid ?? ""),
    queryFn: async (): Promise<WorkspaceMemberRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/members",
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useUpdateWorkspaceMember(workspaceUuid: string) {
  return useOptimisticListMutation<
    WorkspaceMemberRead,
    { memberUserId: string; body: WorkspaceMemberUpdate },
    WorkspaceMemberRead
  >({
    mutationFn: async ({ memberUserId, body }) => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}/members/{member_user_id}",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              member_user_id: memberUserId,
            },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    queryKey: queryKeys.workspaces.members(workspaceUuid),
    applyOptimistic: (current, { memberUserId, body }) =>
      current.map((m) =>
        m.user_id === memberUserId ? { ...m, role: body.role } : m,
      ),
    invalidateKeys: [queryKeys.workspaces.all],
  });
}

export function useRemoveWorkspaceMember(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, WorkspaceMemberRead>({
    mutationFn: async (memberUserId) => {
      const { error } = await apiClient.DELETE(
        "/dashboard/workspaces/{workspace_uuid}/members/{member_user_id}",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              member_user_id: memberUserId,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
    },
    queryKey: queryKeys.workspaces.members(workspaceUuid),
    applyOptimistic: (current, memberUserId) =>
      current.filter((m) => m.user_id !== memberUserId),
    invalidateKeys: [queryKeys.workspaces.list()],
  });
}

export function useWorkspaceInvites(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.invites(workspaceUuid ?? ""),
    queryFn: async (): Promise<WorkspaceInviteRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/invites",
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useCreateWorkspaceInvite(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: WorkspaceInviteCreate,
    ): Promise<WorkspaceInviteCreateResponse> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/invites",
        { params: { path: { workspace_uuid: workspaceUuid } }, body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.invites(workspaceUuid),
      }),
  });
}

export function useRevokeWorkspaceInvite(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, WorkspaceInviteRead>({
    mutationFn: async (inviteUuid) => {
      const { error } = await apiClient.DELETE(
        "/dashboard/workspaces/{workspace_uuid}/invites/{invite_uuid}",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, invite_uuid: inviteUuid },
          },
        },
      );
      if (error) throw new ApiError(error);
    },
    queryKey: queryKeys.workspaces.invites(workspaceUuid),
    applyOptimistic: (current, inviteUuid) =>
      current.map((i) =>
        i.uuid === inviteUuid
          ? { ...i, revoked_at: new Date().toISOString() }
          : i,
      ),
  });
}

export function useResendWorkspaceInvite(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      invite: Pick<WorkspaceInviteRead, "uuid" | "email" | "role">,
    ): Promise<WorkspaceInviteCreateResponse> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/invites",
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body: { email: invite.email, role: invite.role },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.invites(workspaceUuid),
      }),
  });
}

export function useAcceptWorkspaceInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: WorkspaceInviteAcceptRequest,
    ): Promise<WorkspaceRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/invites/accept",
        { body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.workspaces.all }),
  });
}

export function useWorkspaceAudit(
  workspaceUuid: string | undefined,
  params: WorkspaceAuditParams = {},
) {
  return useQuery({
    queryKey: queryKeys.workspaces.audit(workspaceUuid ?? "", params),
    queryFn: async (): Promise<AuditLogRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/audit",
        {
          params: { path: { workspace_uuid: workspaceUuid! }, query: params },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useProjects(
  workspaceUuid: string | undefined,
  q?: string | null,
) {
  const trimmed = q?.trim() || null;
  return useQuery({
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid ?? "", trimmed),
    queryFn: async (): Promise<ProjectRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/projects",
        {
          params: {
            path: { workspace_uuid: workspaceUuid! },
            query: trimmed ? { q: trimmed } : undefined,
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useCreateProject(workspaceUuid: string) {
  return useOptimisticListMutation<ProjectRead, ProjectCreate, ProjectRead>({
    mutationFn: async (body) => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/projects",
        {
          params: { path: { workspace_uuid: workspaceUuid } },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, body) => [
      ...current,
      {
        uuid: `optimistic-${Date.now()}`,
        workspace_uuid: workspaceUuid,
        name: body.name,
        slug: body.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        description: body.description ?? null,
        folder_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ],
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
  });
}

export function useUpdateProject(workspaceUuid: string) {
  const qc = useQueryClient();
  return useOptimisticListMutation<
    ProjectRead,
    { projectUuid: string; body: ProjectUpdate },
    ProjectRead
  >({
    mutationFn: async ({ projectUuid, body }) => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, { projectUuid, body }) =>
      current.map((p) =>
        p.uuid === projectUuid
          ? {
              ...p,
              name: body.name ?? p.name,
              description: body.description ?? p.description,
              updated_at: new Date().toISOString(),
            }
          : p,
      ),
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
    onSuccess: (_data, { projectUuid }) =>
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.projects.detail(
          workspaceUuid,
          projectUuid,
        ),
      }),
  });
}

export function useDeleteProject(workspaceUuid: string) {
  return useOptimisticListMutation<void, string, ProjectRead>({
    mutationFn: async (projectUuid) => {
      const { error } = await apiClient.DELETE(
        "/dashboard/workspaces/{workspace_uuid}/projects/{project_uuid}",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
        },
      );
      if (error) throw new ApiError(error);
    },
    queryKey: queryKeys.workspaces.projects.list(workspaceUuid),
    applyOptimistic: (current, projectUuid) =>
      current.filter((p) => p.uuid !== projectUuid),
    invalidateKeys: [queryKeys.workspaces.projects.listAll(workspaceUuid)],
  });
}

export function useBosunProjects(workspaceUuid: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.list(workspaceUuid ?? ""),
    queryFn: async (): Promise<BosunProjectRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects",
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useBosunProjectReleases(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.releases(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<GitHubReleaseRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/releases",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid && projectUuid),
    retry: false,
  });
}

export function useBosunCockpit(includeArchived = false) {
  return useQuery({
    queryKey: queryKeys.bosun.cockpit(includeArchived),
    queryFn: async (): Promise<BosunCockpitRead> => {
      const { data, error } = await apiClient.GET("/dashboard/me/bosun/cockpit", {
        params: { query: { include_archived: includeArchived } },
      });
      if (error) throw new ApiError(error);
      return data;
    },
  });
}

export function useBosunPortfolioActionItems(
  status?: string | null,
  includeDone = false,
) {
  return useQuery({
    queryKey: queryKeys.bosun.actionItems(status, includeDone),
    queryFn: async (): Promise<BosunUserActionItemRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/me/bosun/action-items",
        {
          params: {
            query: {
              ...(status ? { status } : {}),
              include_done: includeDone,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
  });
}

export function useBosunIntakeRequests(
  workspaceUuid: string | undefined,
  projectUuid?: string | null,
  status?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.intakeRequests(
      workspaceUuid ?? "",
      projectUuid ?? null,
    ),
    queryFn: async (): Promise<BosunIntakeRequestRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/intake-requests",
        {
          params: {
            path: { workspace_uuid: workspaceUuid! },
            query: {
              ...(projectUuid ? { project_uuid: projectUuid } : {}),
              ...(status ? { status } : {}),
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid),
  });
}

export function useCreateBosunIntakeRequest(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunIntakeRequestCreate,
    ): Promise<BosunIntakeRequestRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/intake-requests",
        { params: { path: { workspace_uuid: workspaceUuid } }, body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (request) => {
      qc.invalidateQueries({ queryKey: queryKeys.bosun.cockpit(false) });
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.intakeRequests(
          workspaceUuid,
          request.project_uuid ?? null,
        ),
      });
    },
  });
}

export function useBosunKanbanSummary(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.kanbanSummary(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunKanbanSummaryRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/kanban/summary",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
    retry: false,
  });
}

export function useBosunKanbanTasks(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.kanbanTasks(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunKanbanTaskRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/kanban/tasks",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
    retry: false,
  });
}

export function useCreateBosunProject(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BosunProjectCreate): Promise<BosunProjectRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects",
        { params: { path: { workspace_uuid: workspaceUuid } }, body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (project) => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.all(workspaceUuid),
      });
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.detail(workspaceUuid, project.uuid),
        project,
      );
    },
    retry: false,
  });
}

export function useJob(jobUuid: string | undefined) {
  return useQuery({
    queryKey: ["jobs", jobUuid ?? ""] as const,
    queryFn: async (): Promise<JobRead> => {
      const { data, error } = await apiClient.GET("/dashboard/jobs/{job_uuid}", {
        params: { path: { job_uuid: jobUuid! } },
      });
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(jobUuid),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.is_terminal ? false : 2_000;
    },
    retry: false,
  });
}

export function useValidateBosunWorkspaceCredentials(workspaceUuid: string) {
  return useMutation({
    mutationFn: async (
      body: BosunOrgCredentials,
    ): Promise<BosunWorkspaceCredentialValidationRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/workspace-credentials/validate",
        { params: { path: { workspace_uuid: workspaceUuid } }, body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
  });
}

export function useBosunWorkspaceCredentialStatus(workspaceUuid?: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunCredentials(workspaceUuid ?? ""),
    queryFn: async (): Promise<BosunWorkspaceCredentialStatusRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/workspace-credentials",
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid),
    retry: false,
  });
}

export function useBosunTokenInventory(workspaceUuid?: string) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunTokenInventory(workspaceUuid ?? ""),
    queryFn: async (): Promise<BosunTokenInventoryRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/workspace-credentials/token-inventory",
        { params: { path: { workspace_uuid: workspaceUuid! } } },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid),
    retry: false,
  });
}

export function useSaveBosunWorkspaceCredentials(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunOrgCredentials,
    ): Promise<BosunWorkspaceCredentialStatusRead> => {
      const { data, error } = await apiClient.PUT(
        "/dashboard/workspaces/{workspace_uuid}/bosun/workspace-credentials",
        { params: { path: { workspace_uuid: workspaceUuid } }, body },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (status) => {
      qc.setQueryData(queryKeys.workspaces.bosunCredentials(workspaceUuid), status);
    },
  });
}

export function useBosunProjectLifecycle(
  workspaceUuid: string,
  action: "archive" | "unarchive",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectUuid: string): Promise<BosunProjectLifecycleRead> => {
      const path =
        action === "archive"
          ? "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/archive"
          : "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/unarchive";
      const { data, error } = await apiClient.POST(path, {
        params: {
          path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
        },
      });
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (lifecycle) => {
      qc.setQueryData<BosunProjectRead[]>(
        queryKeys.workspaces.bosunProjects.list(workspaceUuid),
        (current) =>
          (current ?? []).map((project) =>
            project.uuid === lifecycle.project_uuid
              ? {
                  ...project,
                  archive_status: lifecycle.archive_status,
                  archived_at: lifecycle.archived_at,
                  lifecycle_job_uuid: lifecycle.lifecycle_job_uuid,
                  lifecycle_status: lifecycle.lifecycle_status,
                  updated_at: lifecycle.updated_at,
                }
              : project,
          ),
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.detail(
          workspaceUuid,
          lifecycle.project_uuid,
        ),
      });
    },
  });
}

export function useDeleteBosunProject(workspaceUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      body,
    }: {
      projectUuid: string;
      body: BosunProjectDeleteRequest;
    }): Promise<BosunProjectDeleteRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/delete",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (deletion) => {
      qc.setQueryData<BosunProjectRead[]>(
        queryKeys.workspaces.bosunProjects.list(workspaceUuid),
        (current) =>
          (current ?? []).map((project) =>
            project.uuid === deletion.project_uuid
              ? {
                  ...project,
                  deletion_status: deletion.deletion_status,
                  deletion_job_uuid: deletion.deletion_job_uuid,
                  deletion_status_payload: deletion.deletion_status_payload,
                  deleted_at: deletion.deleted_at,
                  updated_at: deletion.updated_at,
                }
              : project,
          ),
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.detail(
          workspaceUuid,
          deletion.project_uuid,
        ),
      });
    },
  });
}

export function useBosunProject(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.detail(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunProjectRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const deletionStatus = query.state.data?.deletion_status;
      return status === "queued" ||
        status === "running" ||
        deletionStatus === "deleting"
        ? 2_000
        : false;
    },
  });
}

export function useUpdateBosunProjectSettings(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunProjectSettingsUpdate,
    ): Promise<BosunProjectRead> => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/settings",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              project_uuid: projectUuid,
            },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (project) => {
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.detail(workspaceUuid, projectUuid),
        project,
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.all(workspaceUuid),
      });
    },
  });
}

export function useSubmitBosunCodexSetup(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunCodexSetupRequest,
    ): Promise<BosunCodexSetupRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/codex-setup",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (result) => {
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.detail(workspaceUuid, projectUuid),
        result.project,
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.list(workspaceUuid),
      });
    },
  });
}

export function useSubmitBosunSlackAppToken(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunSlackAppTokenSubmitRequest,
    ): Promise<BosunProjectRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/slack/app-token",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (project) => {
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.detail(workspaceUuid, projectUuid),
        project,
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.list(workspaceUuid),
      });
    },
  });
}

export function useBosunPmProfile(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.pmProfile(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunProjectManagerProfileRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-profile",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}

export function useUpdateBosunPmProfile(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunProjectManagerProfileUpdate,
    ): Promise<BosunProjectManagerProfileRead> => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-profile",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (profile) =>
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.pmProfile(
          workspaceUuid,
          projectUuid,
        ),
        profile,
      ),
  });
}

export function useBosunProjectGoals(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.goals(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunProjectGoalsRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/goals",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}

export function useBosunPmChat(workspaceUuid: string, projectUuid: string) {
  return useMutation({
    mutationFn: async (
      body: BosunPmChatRequest,
    ): Promise<BosunPmChatResponse> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-chat",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
  });
}

export function useBosunPmThread(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
  conversationUuid?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.pmThreadConversation(
      workspaceUuid ?? "",
      projectUuid ?? "",
      conversationUuid,
    ),
    queryFn: async (): Promise<BosunPmThreadRead> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-chat",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
            query: conversationUuid
              ? { conversation_uuid: conversationUuid }
              : undefined,
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}

export function useBosunPmConversations(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.pmConversations(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<BosunPmConversationRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-chat/conversations",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}

export function useCreateBosunPmConversation(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunPmConversationCreate,
    ): Promise<BosunPmConversationRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-chat/conversations",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.pmConversations(
          workspaceUuid,
          projectUuid,
        ),
      });
    },
  });
}

export function useCreateBosunPmMessage(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunPmMessageCreate,
    ): Promise<BosunPmMessageResponse> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/pm-chat/messages",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.pmThread(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.pmThreadConversation(
          workspaceUuid,
          projectUuid,
          null,
        ),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.pmConversations(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.actionItems(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.cockpit(false) });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.actionItems() });
    },
  });
}

export function useBosunProjectActionItems(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
  status?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.actionItems(
      workspaceUuid ?? "",
      projectUuid ?? "",
      status,
    ),
    queryFn: async (): Promise<BosunUserActionItemRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/action-items",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
            query: status ? { status } : undefined,
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}

export function useCreateBosunProjectActionItem(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunUserActionItemCreate,
    ): Promise<BosunUserActionItemRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/action-items",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.actionItems(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.actionItems() });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.cockpit(false) });
    },
  });
}

export function useUpdateBosunProjectActionItem(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      actionItemUuid,
      body,
    }: {
      actionItemUuid: string;
      body: BosunUserActionItemUpdate;
    }): Promise<BosunUserActionItemRead> => {
      const { data, error } = await apiClient.PATCH(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/action-items/{action_item_uuid}",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              project_uuid: projectUuid,
              action_item_uuid: actionItemUuid,
            },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.actionItems(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.actionItems() });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.cockpit(false) });
    },
  });
}

export function useResolveBosunProjectActionItem(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      actionItemUuid,
      body,
    }: {
      actionItemUuid: string;
      body: BosunUserActionItemResolve;
    }): Promise<BosunUserActionItemRead> => {
      const { data, error } = await apiClient.POST(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/action-items/{action_item_uuid}/resolve",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid,
              project_uuid: projectUuid,
              action_item_uuid: actionItemUuid,
            },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.actionItems(
          workspaceUuid,
          projectUuid,
        ),
      });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.actionItems() });
      qc.invalidateQueries({ queryKey: queryKeys.bosun.cockpit(false) });
    },
  });
}

export function useUpdateBosunProjectGoals(
  workspaceUuid: string,
  projectUuid: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: BosunProjectGoalsUpdate,
    ): Promise<BosunProjectGoalsRead> => {
      const { data, error } = await apiClient.PUT(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/goals",
        {
          params: {
            path: { workspace_uuid: workspaceUuid, project_uuid: projectUuid },
          },
          body,
        },
      );
      if (error) throw new ApiError(error);
      return data;
    },
    onSuccess: (goals) => {
      qc.setQueryData(
        queryKeys.workspaces.bosunProjects.goals(workspaceUuid, projectUuid),
        goals,
      );
      qc.invalidateQueries({
        queryKey: queryKeys.workspaces.bosunProjects.goalsAudit(
          workspaceUuid,
          projectUuid,
        ),
      });
    },
  });
}

export function useBosunProjectGoalsAudit(
  workspaceUuid: string | undefined,
  projectUuid: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.workspaces.bosunProjects.goalsAudit(
      workspaceUuid ?? "",
      projectUuid ?? "",
    ),
    queryFn: async (): Promise<AuditLogRead[]> => {
      const { data, error } = await apiClient.GET(
        "/dashboard/workspaces/{workspace_uuid}/bosun/projects/{project_uuid}/goals/audit",
        {
          params: {
            path: {
              workspace_uuid: workspaceUuid!,
              project_uuid: projectUuid!,
            },
          },
        },
      );
      if (error) throw new ApiError(error);
      return data ?? [];
    },
    enabled: Boolean(workspaceUuid && projectUuid),
  });
}
